/**
 * Bulk repricing batches API client (BFF: `/api/v1/catalog/pricing/price-change-batches`) —
 * epic-pricing-002 Phase 1, plan-platform-002 WP4.3. Confirmed live against
 * `web-merchant-portal-bff`'s `openapi.yml` (2026-09-03): create/list/get/dry-run/apply.
 *
 * **Contract gap (disclosed):** there is no cancel endpoint (no
 * `POST .../price-change-batches/{batchId}/cancel`, unlike markdown plans) even though
 * `BatchStatus` includes `CANCELLED` and the design spec's action column shows a cancel affordance
 * for DRAFT/DRY_RUN_READY/SCHEDULED batches. This module exposes no `cancelPriceChangeBatch`, and
 * the panel renders no cancel action — see `PriceChangeBatchesPanel.tsx`'s header comment.
 */
import { apiClient } from '@/shared/api';

import type {
  CreatePriceChangeBatchRequest,
  ListPriceChangeBatchesParams,
  PriceChangeBatchDetailResponse,
  PriceChangeBatchResponse,
} from '../model/priceBatchTypes';

const BASE = '/api/v1/catalog/pricing/price-change-batches';

export const listPriceChangeBatches = async (
  params: ListPriceChangeBatchesParams = {},
): Promise<PriceChangeBatchResponse[]> =>
  (await apiClient.get<PriceChangeBatchResponse[]>(BASE, { params })).data;

export const createPriceChangeBatch = async (
  body: CreatePriceChangeBatchRequest,
): Promise<PriceChangeBatchResponse> =>
  (await apiClient.post<PriceChangeBatchResponse>(BASE, body)).data;

export const getPriceChangeBatch = async (batchId: string): Promise<PriceChangeBatchDetailResponse> =>
  (await apiClient.get<PriceChangeBatchDetailResponse>(`${BASE}/${encodeURIComponent(batchId)}`)).data;

export const dryRunPriceChangeBatch = async (
  batchId: string,
): Promise<PriceChangeBatchDetailResponse> =>
  (
    await apiClient.post<PriceChangeBatchDetailResponse>(
      `${BASE}/${encodeURIComponent(batchId)}/dry-run`,
    )
  ).data;

export const applyPriceChangeBatch = async (
  batchId: string,
): Promise<PriceChangeBatchDetailResponse> =>
  (
    await apiClient.post<PriceChangeBatchDetailResponse>(
      `${BASE}/${encodeURIComponent(batchId)}/apply`,
    )
  ).data;
